import React, { useCallback, useMemo } from 'react';

import {
  GGChart,
  GGMetric,
  GGSeries,
  GGDateAxis,
  GGMetricsTooltip,
  GGNoSeriesPlaceholder,
  GGReferenceLine,
  Actions,
  Action,
  MultiSelect,
  AddchartIcon,
  CloseIcon,
} from '@gogame/components';
import { dateFNS } from '@gogame/intl-ts';
import { useScreenState, useScreenDispatch } from '@gogame/screens';

import { FILTER_DATE } from '../../constants';
import { useLoadChart, useLoadChartMetrics } from '../../data';
import { ADD_CHART, REMOVE_CHART, SET_CHART_METRICS } from '../../reducers';
import { newChartSchema } from '../../schemas';
import Legend from './components/Legend';
import {
  useChartState,
  useColorGetter,
  useCurrentDimension,
} from './components/hooks';

const Chart = ({ withPaper, tooltipPosStrategy, canAdd, index }) => {
  const chartState = useChartState();
  const temporaryChartState = useChartState(true);
  const { filtersCollection, drillPath, ready } = useScreenState();
  const dispatch = useScreenDispatch();

  const { isLoading, data: chartSeries } = useLoadChart(
    {
      filtersCollection: filtersCollection,
      drillPath: drillPath,
      chartSettings: chartState,
    },
    { enabled: ready, index }
  );

  const colorGetter = useColorGetter(chartSeries);

  const dimension = useCurrentDimension();
  const { isLoading: isMetricsLoading, data: metrics } = useLoadChartMetrics({
    dimension,
  });

  const isCompareMode = useMemo(() => {
    return filtersCollection.length > 1;
  }, [filtersCollection]);

  const dateRanges = useMemo(() => {
    return filtersCollection.map(({ [FILTER_DATE]: date }) => date);
  }, [filtersCollection]);

  const handleRemoveChart = useCallback(
    (index) => () => {
      dispatch({
        type: REMOVE_CHART,
        payload: {
          index,
        },
      });
    },
    [dispatch]
  );

  const handleAddChart = useCallback(() => {
    dispatch({
      type: ADD_CHART,
      payload: newChartSchema.cast(),
    });
  }, [dispatch]);

  const canDelete = useMemo(() => index > 0, [index]);

  const daySeparators = useMemo(() => {
    const period = chartSeries?.[0]?.period;
    if (!period) {
      return [];
    }
    const diff = Math.abs(
      dateFNS.differenceInDays(new Date(period[0]), new Date(period[1]))
    );

    let result = [];
    for (let i = 1; i <= diff; i++) {
      result.push(<GGReferenceLine x={24 * i} stroke={'#666'} />);
    }
    return result;
  }, [chartSeries]);

  const handleChangeMetrics = (_, v) => {
    dispatch({
      type: SET_CHART_METRICS,
      payload: {
        index: index,
        value: v,
      },
    });
  };

  return (
    <GGChart
      loading={isLoading}
      index={index}
      legend={false}
      compareMode={isCompareMode}
      style={{ height: '100%', margin: 0 }}
      withPaper={withPaper}
      colorGetter={colorGetter}
      scale={'hourly'}
      affix={
        <MultiSelect
          style={{ maxWidth: '216px', marginRight: '16px' }}
          options={metrics}
          label={'Metrics'}
          value={temporaryChartState.metrics}
          onChange={handleChangeMetrics}
          dropdownWidth={274}
          loading={isMetricsLoading}
          multipleSearch={true}
        />
      }
      actions={
        <Actions>
          {canAdd ? (
            <Action
              icon={<AddchartIcon />}
              onClick={handleAddChart}
              label={'Add Chart'}
            />
          ) : null}
          {canDelete ? (
            <Action
              icon={<CloseIcon />}
              onClick={handleRemoveChart(index)}
              label={'Remove Chart'}
            />
          ) : null}
        </Actions>
      }
    >
      {chartSeries?.map(({ period, rows, metrics, chartId }) => (
        <GGSeries
          data={rows}
          period={period}
          seriesKey={chartId}
          key={chartId}
          dateKey={'Date'}
        >
          {metrics.map((metric) => (
            <GGMetric
              key={metric.id}
              dataKey={metric.id}
              colorKey={metric.id}
              yAxisGroup={metric.yAxisGroup}
              valueType={metric.type}
              name={metric.name}
              compared={metric.compared}
            />
          ))}
        </GGSeries>
      ))}
      <GGMetricsTooltip
        labelTemplate={'HH:mm'}
        posStrategy={tooltipPosStrategy}
        compareDisabled={true}
      />
      <GGDateAxis
        template={'HH:mm'}
        compareDisabled={true}
        minTickGap={20}
        allowDuplicatedCategory={false}
      />

      {daySeparators}
      <GGNoSeriesPlaceholder
        align={'center'}
        text={
          ready ? (
            'There is no chart data for selected filters'
          ) : (
            <span>
              Please choose one of your Presets or select metrics, filters and
              graph settings, in order to load data. <br /> Please note that
              data will load, only once you click “Apply”.
            </span>
          )
        }
      />
      <Legend dateRanges={dateRanges} />
    </GGChart>
  );
};

export default Chart;
